import { openDB, DBSchema, IDBPDatabase } from 'idb'

interface QueuedRequest {
  id: string
  url: string
  method: string
  body: string | null
  headers: Record<string, string>
  timestamp: number
  retryCount: number
}

interface OfflineDB extends DBSchema {
  requests: {
    key: string
    value: QueuedRequest
    indexes: { 'by-timestamp': number }
  }
}

const DB_NAME = 'copilot-core-offline'
const DB_VERSION = 1
const MAX_RETRIES = 3

class OfflineQueue {
  private dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null

  private getDB(): Promise<IDBPDatabase<OfflineDB>> {
    if (!this.dbPromise) {
      this.dbPromise = openDB<OfflineDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          const store = db.createObjectStore('requests', { keyPath: 'id' })
          store.createIndex('by-timestamp', 'timestamp')
        },
      })
    }
    return this.dbPromise
  }

  /**
   * Queue a request for later execution when online
   */
  async enqueue(url: string, options: RequestInit): Promise<string> {
    const db = await this.getDB()
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

    const headers: Record<string, string> = {}
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value
        })
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value
        })
      } else {
        Object.assign(headers, options.headers)
      }
    }

    // Add default content-type for JSON bodies
    if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    const request: QueuedRequest = {
      id,
      url,
      method: options.method || 'GET',
      body: typeof options.body === 'string' ? options.body : null,
      headers,
      timestamp: Date.now(),
      retryCount: 0,
    }

    await db.put('requests', request)
    return id
  }

  /**
   * Process all queued requests
   * Returns count of successful and failed requests
   */
  async processQueue(): Promise<{ success: number; failed: number }> {
    const db = await this.getDB()
    const requests = await db.getAllFromIndex('requests', 'by-timestamp')

    let success = 0
    let failed = 0

    for (const request of requests) {
      try {
        const response = await fetch(request.url, {
          method: request.method,
          body: request.body,
          headers: request.headers,
        })

        if (response.ok) {
          await db.delete('requests', request.id)
          success++
        } else if (response.status >= 400 && response.status < 500) {
          // Client error - don't retry, remove from queue
          await db.delete('requests', request.id)
          failed++
        } else {
          // Server error - increment retry count
          request.retryCount++
          if (request.retryCount >= MAX_RETRIES) {
            await db.delete('requests', request.id)
            failed++
          } else {
            await db.put('requests', request)
          }
        }
      } catch {
        // Network error - increment retry count
        request.retryCount++
        if (request.retryCount >= MAX_RETRIES) {
          await db.delete('requests', request.id)
          failed++
        } else {
          await db.put('requests', request)
        }
      }
    }

    return { success, failed }
  }

  /**
   * Get count of pending requests
   */
  async getPendingCount(): Promise<number> {
    const db = await this.getDB()
    return db.count('requests')
  }

  /**
   * Get all pending requests (for debugging/inspection)
   */
  async getPendingRequests(): Promise<QueuedRequest[]> {
    const db = await this.getDB()
    return db.getAllFromIndex('requests', 'by-timestamp')
  }

  /**
   * Clear all queued requests
   */
  async clear(): Promise<void> {
    const db = await this.getDB()
    await db.clear('requests')
  }

  /**
   * Remove a specific request by ID
   */
  async remove(id: string): Promise<void> {
    const db = await this.getDB()
    await db.delete('requests', id)
  }
}

export const offlineQueue = new OfflineQueue()

// Auto-sync on reconnect
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    offlineQueue.processQueue()
  })
}

export type { QueuedRequest }
